export type Locale = 'uk' | 'ru' | 'en';

export interface SiteCopy {
  languageLabel: string;
  nav: {
    about: string;
    approach: string;
    lessons: string;
    teacher: string;
    contact: string;
    test: string;
  };
  hero: {
    eyebrow: string;
    title: string;
    accent: string;
    body: string;
    primary: string;
    secondary: string;
    note: string;
  };
  proof: Array<{ value: string; label: string }>;
  approach: {
    eyebrow: string;
    title: string;
    body: string;
    cards: Array<{ number: string; title: string; body: string }>;
  };
  lessons: {
    eyebrow: string;
    title: string;
    body: string;
    cards: Array<{ tag: string; price: string; title: string; body: string; meta: string }>;
  };
  teacher: {
    eyebrow: string;
    title: string;
    body: string;
    quote: string;
    signature: string;
  };
  assessment: {
    eyebrow: string;
    title: string;
    body: string;
    points: string[];
    action: string;
    privacy: string;
  };
  footer: {
    motto: string;
    gate: string;
    rights: string;
  };
  test: {
    eyebrow: string;
    title: string;
    intro: string;
    nameLabel: string;
    namePlaceholder: string;
    phoneLabel: string;
    phonePlaceholder: string;
    consentLabel: string;
    detailsError: string;
    shareTitle: string;
    shareIntro: string;
    shareSubmit: string;
    shareSent: string;
    shareError: string;
    sending: string;
    start: string;
    loading: string;
    loadingError: string;
    question: string;
    of: string;
    answered: string;
    previous: string;
    next: string;
    submit: string;
    submitting: string;
    selectAnswer: string;
    validationError: string;
    serverError: string;
    approximate: string;
    resultTitle: string;
    scoreLabel: string;
    resultCopy: string;
    contactTitle: string;
    contactBody: string;
    phone: string;
    telegram: string;
    retake: string;
    home: string;
    secureNote: string;
  };
  notFound: {
    title: string;
    body: string;
    action: string;
  };
}

export const copy: Record<Locale, SiteCopy> = {
  uk: {
    languageLabel: 'Мова сайту',
    nav: {
      about: 'Про школу',
      approach: 'Підхід',
      lessons: 'Формати',
      teacher: 'Викладачка',
      contact: 'Контакти',
      test: 'Тест рівня',
    },
    hero: {
      eyebrow: 'Онлайн-школа англійської мови',
      title: 'Англійська, що',
      accent: 'звучить упевнено.',
      body: 'Персональні заняття для тих, хто хоче не просто знати правила, а вільно говорити, працювати й подорожувати англійською.',
      primary: 'Визначити свій рівень',
      secondary: 'Як ми навчаємо',
      note: '30 запитань · близько 12 хвилин · без реєстрації',
    },
    proof: [
      { value: '1:1', label: 'увага викладачки' },
      { value: '3×', label: 'більше живої практики' },
      { value: 'A1—C1', label: 'рівні навчання' },
    ],
    approach: {
      eyebrow: 'Методика Queen English Academy',
      title: 'Англійська для реального життя.',
      body: 'Сучасна програма, адаптована до вашого рівня, цілей і темпу.',
      cards: [
        { number: '01', title: 'Системний підхід', body: 'У Queen English Academy ми навчаємо англійської системно, за міжнародно визнаними методиками, перевіреними багаторічною практикою.' },
        { number: '02', title: 'Комунікативна методика', body: 'Більшу частину уроку говорите ви. Викладач спрямовує розмову, пояснює складне й допомагає впевнено використовувати англійську в реальних ситуаціях. Максимум практики — без нудних лекцій.' },
        { number: '03', title: 'На уроках ми', body: 'Говоримо англійською, просто пояснюємо граматику, розширюємо словник, працюємо над вимовою та використовуємо інтерактивні завдання — у комфортній атмосфері без страху помилок.' },
      ],
    },
    lessons: {
      eyebrow: 'Формати занять',
      title: 'Обирайте ритм, який вписується у ваше життя.',
      body: 'Усі заняття проходять онлайн, містять персональні матеріали та короткий підсумок прогресу.',
      cards: [
        { tag: 'Перший крок', price: '250 грн', title: 'Пробне заняття', body: 'Познайомимося, визначимо ваш рівень і цілі та складемо персональний план навчання.', meta: '20–30 хв' },
        { tag: 'Персонально', price: '500 грн', title: 'Індивідуальне заняття', body: 'Урок один на один із персональними матеріалами, практикою та детальним зворотним звʼязком.', meta: '60 хв' },
        { tag: 'Найвигідніше', price: '3 600 грн', title: 'Абонемент', body: 'Вісім індивідуальних занять для системного прогресу за вигіднішою ціною.', meta: '8 занять × 60 хв · 450 грн за заняття' },
      ],
    },
    teacher: {
      eyebrow: 'Ваша викладачка',
      title: 'Поруч, поки англійська не стане мовою ваших можливостей.',
      body: 'Зрозумілі пояснення, підтримка й уроки, адаптовані саме до вас. Тут помилки — це нормальна частина навчання та руху вперед.',
      quote: 'Моя мета — щоб англійська стала вашим інструментом свободи. Адже світ — це шахівниця. А англійська, як королева в ньому, дає можливість зробити будь-який хід.',
      signature: 'Queen English Academy',
    },
    assessment: {
      eyebrow: 'Безкоштовний тест',
      title: 'Почнімо з вашої точки A.',
      body: 'Дайте відповіді на 30 запитань із граматики та лексики. Після тесту ви одразу побачите орієнтовний рівень і зможете обрати наступний крок.',
      points: ['Результат одразу', 'Без реєстрації', 'Рекомендація після тесту'],
      action: 'Пройти тест рівня',
      privacy: 'Правильні відповіді перевіряються захищено на сервері.',
    },
    footer: {
      motto: 'English with confidence.',
      gate: 'Напишіть або зателефонуйте, щоб обрати формат і час першого заняття.',
      rights: 'Усі права захищено.',
    },
    test: {
      eyebrow: 'Queen English Level Check',
      title: 'Дізнайтеся свій орієнтовний рівень англійської.',
      intro: '30 коротких запитань перевірять граматику та словниковий запас від A1 до C1. Обирайте одну відповідь і не користуйтеся перекладачем — так результат буде точнішим.',
      nameLabel: 'Ваше імʼя',
      namePlaceholder: 'Імʼя та прізвище',
      phoneLabel: 'Номер телефону',
      phonePlaceholder: '+380 00 000 00 00',
      consentLabel: 'Я погоджуюся, що мої контактні дані та результати тесту буде надіслано викладачці для звʼязку щодо занять.',
      detailsError: 'Вкажіть коректні імʼя та номер телефону й підтвердьте згоду.',
      shareTitle: 'Надіслати результат викладачці',
      shareIntro: 'Разом із результатами тесту викладачці буде передано ваші контактні дані, щоб вона могла проаналізувати відповіді, надати рекомендації та зв’язатися з вами. Детальний розбір результатів із зазначенням правильних і неправильних відповідей можна отримати безпосередньо у викладачки.',
      shareSubmit: 'Надіслати результат',
      shareSent: 'Результат і контактні дані надіслано викладачці.',
      shareError: 'Не вдалося надіслати результат. Спробуйте ще раз за хвилину.',
      sending: 'Надсилаємо…',
      start: 'Почати тест',
      loading: 'Готуємо ваш тест…',
      loadingError: 'Не вдалося завантажити тест. Спробуйте оновити сторінку.',
      question: 'Запитання',
      of: 'з',
      answered: 'відповіли',
      previous: 'Назад',
      next: 'Далі',
      submit: 'Отримати результат',
      submitting: 'Перевіряємо відповіді…',
      selectAnswer: 'Оберіть відповідь, щоб продовжити.',
      validationError: 'Для перевірки потрібно відповісти на всі 30 запитань.',
      serverError: 'Не вдалося перевірити відповіді. Спробуйте ще раз за хвилину.',
      approximate: 'Орієнтовний рівень',
      resultTitle: 'Ваш результат готовий',
      scoreLabel: 'правильних відповідей',
      resultCopy: 'Це коротка діагностика. На першому занятті ми додатково перевіримо говоріння та сприйняття на слух.',
      contactTitle: 'Зробіть наступний крок',
      contactBody: 'Напишіть викладачці, щоб узгодити мету та час першого заняття.',
      phone: 'Телефон',
      telegram: 'Telegram',
      retake: 'Пройти ще раз',
      home: 'На головну',
      secureNote: 'Результат, імʼя та телефон буде надіслано лише після вашої згоди.',
    },
    notFound: { title: 'Сторінку не знайдено', body: 'Схоже, це посилання більше не працює.', action: 'Повернутися на головну' },
  },
  ru: {
    languageLabel: 'Язык сайта',
    nav: { about: 'О школе', approach: 'Подход', lessons: 'Форматы', teacher: 'Преподаватель', contact: 'Контакты', test: 'Тест уровня' },
    hero: {
      eyebrow: 'Онлайн-школа английского языка',
      title: 'Английский, который',
      accent: 'звучит уверенно.',
      body: 'Персональные занятия для тех, кто хочет не просто знать правила, а свободно говорить, работать и путешествовать на английском.',
      primary: 'Определить свой уровень',
      secondary: 'Как мы учим',
      note: '30 вопросов · около 12 минут · без регистрации',
    },
    proof: [
      { value: '1:1', label: 'внимание преподавателя' },
      { value: '3×', label: 'больше живой практики' },
      { value: 'A1—C1', label: 'уровни обучения' },
    ],
    approach: {
      eyebrow: 'Методика Queen English Academy',
      title: 'Английский для реальной жизни.',
      body: 'Современная программа, адаптированная к вашему уровню, целям и темпу.',
      cards: [
        { number: '01', title: 'Системный подход', body: 'В Queen English Academy мы обучаем английскому системно, по международно признанным методикам, проверенным многолетней практикой.' },
        { number: '02', title: 'Коммуникативная методика', body: 'Большую часть урока говорите вы. Преподаватель направляет разговор, объясняет сложное и помогает уверенно использовать английский в реальных ситуациях. Максимум практики — без скучных лекций.' },
        { number: '03', title: 'На уроках мы', body: 'Говорим на английском, просто объясняем грамматику, расширяем словарь, работаем над произношением и используем интерактивные задания — в комфортной атмосфере без страха ошибок.' },
      ],
    },
    lessons: {
      eyebrow: 'Форматы занятий',
      title: 'Выбирайте ритм, который вписывается в вашу жизнь.',
      body: 'Все занятия проходят онлайн, включают персональные материалы и короткий итог прогресса.',
      cards: [
        { tag: 'Первый шаг', price: '250 грн', title: 'Пробный урок', body: 'Познакомимся, определим ваш уровень и цели и составим персональный план обучения.', meta: '20–30 мин' },
        { tag: 'Персонально', price: '500 грн', title: 'Индивидуальное занятие', body: 'Урок один на один с персональными материалами, практикой и подробной обратной связью.', meta: '60 мин' },
        { tag: 'Самый выгодный', price: '3 600 грн', title: 'Абонемент', body: 'Восемь индивидуальных занятий для системного прогресса по более выгодной цене.', meta: '8 занятий × 60 мин · 450 грн за занятие' },
      ],
    },
    teacher: {
      eyebrow: 'Ваш преподаватель',
      title: 'Рядом, пока английский не станет языком ваших возможностей.',
      body: 'Понятные объяснения, поддержка и уроки, адаптированные именно для вас. Здесь ошибки — это нормальная часть обучения и движения вперёд.',
      quote: 'Моя цель — чтобы английский стал вашим инструментом свободы. Ведь мир — это шахматная доска. А английский, словно королева на ней, даёт возможность сделать любой ход.',
      signature: 'Queen English Academy',
    },
    assessment: {
      eyebrow: 'Бесплатный тест',
      title: 'Начнём с вашей точки A.',
      body: 'Ответьте на 30 вопросов по грамматике и лексике. После теста вы сразу увидите примерный уровень и сможете выбрать следующий шаг.',
      points: ['Результат сразу', 'Без регистрации', 'Рекомендация после теста'],
      action: 'Пройти тест уровня',
      privacy: 'Правильные ответы проверяются безопасно на сервере.',
    },
    footer: { motto: 'English with confidence.', gate: 'Напишите или позвоните, чтобы выбрать формат и время первого занятия.', rights: 'Все права защищены.' },
    test: {
      eyebrow: 'Queen English Level Check', title: 'Узнайте свой примерный уровень английского.', intro: '30 коротких вопросов проверят грамматику и словарный запас от A1 до C1. Выбирайте один ответ и не пользуйтесь переводчиком — так результат будет точнее.', nameLabel: 'Ваше имя', namePlaceholder: 'Имя и фамилия', phoneLabel: 'Номер телефона', phonePlaceholder: '+380 00 000 00 00', consentLabel: 'Я соглашаюсь, что мои контактные данные и результаты теста будут отправлены преподавателю для связи по поводу занятий.', detailsError: 'Укажите корректные имя и номер телефона и подтвердите согласие.', shareTitle: 'Отправить результат преподавателю', shareIntro: 'Это необязательно. Вместе с результатом будут отправлены ваше имя и номер телефона, чтобы преподаватель мог связаться с вами.', shareSubmit: 'Отправить результат', shareSent: 'Результат и контактные данные отправлены преподавателю.', shareError: 'Не удалось отправить результат. Попробуйте ещё раз через минуту.', sending: 'Отправляем…', start: 'Начать тест', loading: 'Готовим ваш тест…', loadingError: 'Не удалось загрузить тест. Обновите страницу.', question: 'Вопрос', of: 'из', answered: 'отвечено', previous: 'Назад', next: 'Далее', submit: 'Получить результат', submitting: 'Проверяем ответы…', selectAnswer: 'Выберите ответ, чтобы продолжить.', validationError: 'Для проверки нужно ответить на все 30 вопросов.', serverError: 'Не удалось проверить ответы. Попробуйте ещё раз через минуту.', approximate: 'Примерный уровень', resultTitle: 'Ваш результат готов', scoreLabel: 'правильных ответов', resultCopy: 'Это короткая диагностика. На первом занятии мы дополнительно проверим говорение и понимание на слух.', contactTitle: 'Сделайте следующий шаг', contactBody: 'Напишите преподавателю, чтобы согласовать цель и время первого занятия.', phone: 'Телефон', telegram: 'Telegram', retake: 'Пройти ещё раз', home: 'На главную', secureNote: 'Результат, имя и телефон будут отправлены только после вашего согласия.',
    },
    notFound: { title: 'Страница не найдена', body: 'Похоже, эта ссылка больше не работает.', action: 'Вернуться на главную' },
  },
  en: {
    languageLabel: 'Site language',
    nav: { about: 'About', approach: 'Approach', lessons: 'Lessons', teacher: 'Teacher', contact: 'Contacts', test: 'Level test' },
    hero: {
      eyebrow: 'Online English school',
      title: 'English that',
      accent: 'sounds confident.',
      body: 'Personal lessons for people who want more than rules — they want to speak freely, work internationally, and travel with confidence.',
      primary: 'Check my level',
      secondary: 'How we teach',
      note: '30 questions · about 12 minutes · no sign-up',
    },
    proof: [
      { value: '1:1', label: 'teacher attention' },
      { value: '3×', label: 'more active practice' },
      { value: 'A1—C1', label: 'learning levels' },
    ],
    approach: {
      eyebrow: 'The Queen English Academy Method',
      title: 'English for real life.',
      body: 'A modern programme adapted to your level, goals, and pace.',
      cards: [
        { number: '01', title: 'A structured approach', body: 'At Queen English Academy, we teach English systematically using internationally recognised methods proven through years of classroom practice.' },
        { number: '02', title: 'Communicative teaching', body: 'You do most of the speaking. Your teacher guides the conversation, explains difficult points, and helps you use English confidently in real situations. Maximum practice, with no tedious lectures.' },
        { number: '03', title: 'In every lesson, we', body: 'Speak English, explain grammar simply, build vocabulary, improve pronunciation, and use interactive activities — all in a comfortable atmosphere where mistakes are welcome.' },
      ],
    },
    lessons: {
      eyebrow: 'Lesson formats',
      title: 'Choose a rhythm that fits your life.',
      body: 'Every lesson is online and includes personal materials plus a concise progress recap.',
      cards: [
        { tag: 'First step', price: '250 грн', title: 'Trial lesson', body: 'Meet your teacher, identify your level and goals, and receive a personal learning plan.', meta: '20–30 min' },
        { tag: 'Personal', price: '500 грн', title: 'Individual lesson', body: 'A one-to-one lesson with personal materials, focused practice, and detailed feedback.', meta: '60 min' },
        { tag: 'Best value', price: '3 600 грн', title: '8-lesson package', body: 'Eight individual lessons for consistent progress at a better per-lesson price.', meta: '8 lessons × 60 min · 450 грн per lesson' },
      ],
    },
    teacher: {
      eyebrow: 'Your teacher',
      title: 'By your side until English becomes the language of your opportunities.',
      body: 'Clear explanations, support, and lessons adapted specifically to you. Here, mistakes are a natural part of learning and moving forward.',
      quote: 'My goal is for English to become your tool for freedom. The world is a chessboard, and English, like the queen on it, gives you the ability to make any move.',
      signature: 'Queen English Academy',
    },
    assessment: {
      eyebrow: 'Free level check',
      title: 'Let’s begin at your point A.',
      body: 'Answer 30 grammar and vocabulary questions. You will see an approximate level straight away and choose your next step.',
      points: ['Instant result', 'No registration', 'Recommendation after the test'],
      action: 'Take the level test',
      privacy: 'Correct answers are checked securely on the server.',
    },
    footer: { motto: 'English with confidence.', gate: 'Message or call to choose a format and time for your first lesson.', rights: 'All rights reserved.' },
    test: {
      eyebrow: 'Queen English Level Check', title: 'Discover your approximate English level.', intro: '30 short questions will check grammar and vocabulary from A1 to C1. Choose one answer and avoid a translator so your result is more useful.', nameLabel: 'Your name', namePlaceholder: 'First and last name', phoneLabel: 'Phone number', phonePlaceholder: '+380 00 000 00 00', consentLabel: 'I agree that my contact details and test results will be sent to the teacher so she can contact me about lessons.', detailsError: 'Enter a valid name and phone number and confirm your consent.', shareTitle: 'Send my result to the teacher', shareIntro: 'This is optional. Your name and phone number will be sent with the result so the teacher can contact you.', shareSubmit: 'Send my result', shareSent: 'Your result and contact details were sent to the teacher.', shareError: 'We could not send your result. Please try again in a minute.', sending: 'Sending…', start: 'Start the test', loading: 'Preparing your test…', loadingError: 'We could not load the test. Please refresh the page.', question: 'Question', of: 'of', answered: 'answered', previous: 'Back', next: 'Next', submit: 'Get my result', submitting: 'Checking your answers…', selectAnswer: 'Choose an answer to continue.', validationError: 'Please answer all 30 questions before submitting.', serverError: 'We could not check your answers. Please try again in a minute.', approximate: 'Approximate level', resultTitle: 'Your result is ready', scoreLabel: 'correct answers', resultCopy: 'This is a short diagnostic. In your first lesson, we will also check speaking and listening.', contactTitle: 'Take the next step', contactBody: 'Message your teacher to choose a goal and time for your first lesson.', phone: 'Phone', telegram: 'Telegram', retake: 'Retake the test', home: 'Back home', secureNote: 'Your result, name, and phone number are sent only after you consent.',
    },
    notFound: { title: 'Page not found', body: 'It looks like this link no longer works.', action: 'Back to the homepage' },
  },
};
